function getLocalizedUrl(path) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
}

document.addEventListener("DOMContentLoaded", function () {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  // All drawer listeners are delegated and bound exactly once (the #cart-drawer
  // element persists; only its innerHTML is replaced on refresh), so nothing
  // accumulates across re-renders.
  let listenersBound = false;
  let isQuickAdding = false;
  let loadingDepth = 0; // >0 while any fetch/refresh for the drawer is in flight
  let refreshSeq = 0; // latest refresh wins; stale responses are dropped

  // Global variable to track pending quantity updates
  let pendingQuantityUpdate = null;
  // Store pending timeouts for each cart item (key = itemKey, value = timeout ID)
  const pendingTimeouts = new Map();

  const QUICK_ADD_FORM_SELECTOR =
    "form[data-cart-quick-add], form[action$='/cart/add']";

  bindDrawerListeners();
  initializeCartDrawer();
  // Lets other scripts (e.g. the PDP upgrade rows) re-render the drawer + header count
  // without opening it.
  window.refreshCartDrawer = () => refreshCartContent({ quiet: true });

  // When user clicks browser back (or tab becomes visible), refresh cart so drawer shows latest content
  let lastCartRefreshAt = 0;
  const CART_REFRESH_THROTTLE_MS = 2000;
  function refreshCartWhenPageShown() {
    const now = Date.now();
    if (now - lastCartRefreshAt < CART_REFRESH_THROTTLE_MS) return;
    lastCartRefreshAt = now;
    refreshCartContent();
  }
  // A fresh page load already has a server-rendered drawer; only refresh when the page came back via
  // back/forward (bfcache restore or a possibly HTTP-cached copy). Throttle avoids duplicate work.
  window.addEventListener("pageshow", function (event) {
    const nav = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    if (event.persisted || (nav && nav.type === "back_forward")) refreshCartWhenPageShown();
  });
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") refreshCartWhenPageShown();
  });

  // Subscribe to cart updates if available
  if (typeof subscribe === "function") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
    subscribe(PUB_SUB_EVENTS.cartError, handleCartError);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  function getDrawer() {
    return document.getElementById("cart-drawer");
  }

  function getDrawerBody() {
    const drawer = getDrawer();
    return drawer ? drawer.querySelector(".cart-drawer__items") : null;
  }

  function openDrawer() {
    if (typeof window.openCartDrawer === "function") window.openCartDrawer();
  }

  function closeDrawer() {
    if (typeof window.closeCartDrawer === "function") window.closeCartDrawer();
  }

  function canPublish() {
    return typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined";
  }

  /** Pull variant ids out of a /cart/add.js response (single item or {items}). */
  function extractVariantIds(payload) {
    if (!payload) return [];
    const list = Array.isArray(payload.items) ? payload.items : [payload];
    return list
      .map((it) => it && (it.variant_id || it.id))
      .filter(Boolean)
      .map(String);
  }

  // ---------------------------------------------------------------------------
  // Listeners (bound once)
  // ---------------------------------------------------------------------------
  function bindDrawerListeners() {
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener("click", onDocumentClick);
    document.addEventListener("input", onQuantityInput);
    document.addEventListener("submit", onQuickAddSubmit);
  }

  function onDocumentClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;

    // Open Cart triggers (anywhere on the page)
    if (target.closest("[data-open-cart]")) {
      openDrawer();
    }

    if (!target.closest("#cart-drawer")) return;

    // Close
    if (target.closest(".cart-drawer__close")) {
      closeDrawer();
      return;
    }

    // Remove
    const removeBtn = target.closest("[data-remove-item]");
    if (removeBtn) {
      e.preventDefault();
      if (isDrawerBusy()) return;
      handleRemoveItem(removeBtn);
      return;
    }

    // Checkout
    if (target.closest("[data-cart-checkout]")) {
      e.preventDefault();
      if (isDrawerBusy()) return;
      ensureBogoEvenThenCheckout();
      return;
    }

    // Cross-sell row button ("+ Add": adds the suggested product as a new line)
    const upgBtn = target.closest("[data-cupg]");
    if (upgBtn) {
      e.preventDefault();
      handleCrossSellAdd(upgBtn);
      return;
    }

    // Quantity
    const upBtn = target.closest("[data-quantity-up]");
    if (upBtn) {
      e.preventDefault();
      onQuantityStep(upBtn, +1);
      return;
    }
    const downBtn = target.closest("[data-quantity-down]");
    if (downBtn) {
      e.preventDefault();
      onQuantityStep(downBtn, -1);
      return;
    }
  }

  async function onQuickAddSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest("#cart-drawer") || !form.matches(QUICK_ADD_FORM_SELECTOR)) return;
    e.preventDefault();
    await handleQuickAdd(form);
  }

  // ---------------------------------------------------------------------------
  // Per-render init (safe to call after every refresh — binds no listeners on
  // persistent elements)
  // ---------------------------------------------------------------------------
  function initializeCartDrawer() {
    if (!getDrawer()) return;
    initRecommendationsSlider();
    updateCartDrawerBusyState();
  }

  async function handleCartUpdate(data) {
    if (!data || data.action !== "add") return;
    // Adds made from inside the drawer refresh it themselves.
    if (data.source === "cart-drawer") return;
    // Open first so the shopper sees the skeleton while the drawer re-renders.
    openDrawer();
    await refreshCartContent({ ghostVariantIds: extractVariantIds(data.variant) });
  }

  // ---------------------------------------------------------------------------
  // Quantity controls (debounced)
  // ---------------------------------------------------------------------------
  function onQuantityStep(button, direction) {
    if (isDrawerBusy()) return;
    const quantityControl = button.closest(".cart-item");
    if (!quantityControl) return;
    const input = quantityControl.querySelector("[data-quantity-input]");
    if (!input) return;
    const itemKey = quantityControl.dataset.itemId;
    const maxQuantity = parseInt(quantityControl.dataset.maxQuantity);

    let newQuantity;
    if (direction > 0) {
      newQuantity = parseInt(input.value) + 1;
      // Validate against stock
      if (maxQuantity > 0 && newQuantity > maxQuantity) {
        showStockError(quantityControl, `Only ${maxQuantity} available`);
        newQuantity = maxQuantity;
      }
    } else {
      // BOGO: will rebalance in handleQuantityChange (reduce total by 2, split)
      if (quantityControl.dataset.bogo === "true") {
        quantityControl.dataset.bogoDown = "true";
      }
      newQuantity = parseInt(input.value) - 1;
      if (newQuantity < 1) newQuantity = 1;
    }

    // Update input value immediately for visual feedback
    input.value = newQuantity;
    input.classList.add("updating");
    updateCartDrawerBusyState();

    // Clear any existing timeout for this item
    if (pendingTimeouts.has(itemKey)) {
      clearTimeout(pendingTimeouts.get(itemKey));
    }

    // Set new timeout to actually update the cart
    const timeoutId = setTimeout(async () => {
      pendingTimeouts.delete(itemKey);
      quantityControl.classList.add("updating");
      updateCartDrawerBusyState();
      try {
        await handleQuantityChange(button, 0);
      } finally {
        quantityControl.classList.remove("updating");
        input.classList.remove("updating");
        updateCartDrawerBusyState();
      }
    }, 500); // 500ms delay

    pendingTimeouts.set(itemKey, timeoutId);
  }

  // Function declaration (hoisted) so bindDrawerListeners() can reference it
  // before this point; the debounced worker is created lazily, once.
  let debouncedQuantityInput = null;
  function onQuantityInput(e) {
    const t = e.target;
    if (!(t instanceof Element) || !t.matches("#cart-drawer [data-quantity-input]")) return;
    if (!debouncedQuantityInput) {
      debouncedQuantityInput =
        typeof debounce === "function" ? debounce(handleQuantityInputEvent, 750) : handleQuantityInputEvent;
    }
    debouncedQuantityInput(e);
  }

  async function handleQuantityInputEvent(e) {
    const input = e.target;
    if (!(input instanceof Element) || !input.matches("[data-quantity-input]")) return;
    // The drawer was re-rendered meanwhile (e.g. a cross-sell add): this input is stale.
    if (!input.isConnected || crossSellBusy) return;
    const quantityControl = input.closest("#cart-drawer .cart-item");
    if (!quantityControl) return;
    const maxQuantity = parseInt(quantityControl.dataset.maxQuantity);
    let newQuantity = parseInt(input.value) || 1;

    if (maxQuantity > 0 && newQuantity > maxQuantity) {
      showStockError(quantityControl, `Only ${maxQuantity} available`);
      newQuantity = maxQuantity;
    }

    if (pendingQuantityUpdate) return;
    pendingQuantityUpdate = true;
    try {
      await handleQuantityChange(input, 0);
    } finally {
      pendingQuantityUpdate = false;
    }
  }

  function showStockError(element, message) {
    const existingError = element.querySelector(".stock-error");
    if (!existingError) return;
    existingError.textContent = message;
    // Auto-remove after 3 seconds
    setTimeout(() => {
      existingError.textContent = "";
    }, 3000);
  }

  function updateCartDrawerBusyState() {
    const cartDrawer = getDrawer();
    if (!cartDrawer) return;
    const hasBusy = cartDrawer.querySelector(".cart-item.updating, .cart-item.removing");
    cartDrawer.classList.toggle("cart-drawer--busy", !!hasBusy || loadingDepth > 0);
  }

  // ---------------------------------------------------------------------------
  // Loading skeleton
  // ---------------------------------------------------------------------------
  function ghostRowMarkup() {
    return (
      '<div class="cart-item cart-item--ghost" aria-hidden="true">' +
      '<div class="cart-item__image"></div>' +
      '<div class="cart-item__details">' +
      '<div class="cart-item__info"><div class="left_item-info"><span class="cart-item__title">&nbsp;</span></div>' +
      '<p class="cart-item__price">&nbsp;</p></div>' +
      '<div class="cart-item__actions"><div class="quantity-wrapper"><div class="cart-item__quantity"></div></div>' +
      '<span class="cart-item__remove"></span></div>' +
      "</div></div>"
    );
  }

  /** Placeholder rows for lines that are about to appear (not yet rendered). */
  function addGhostRows(body, variantIds) {
    if (body.querySelector(".cart-item--ghost, .cart-drawer__ghost")) return;
    const ids = Array.isArray(variantIds) ? variantIds : [];
    const list = body.querySelector(".cart__items");

    if (list) {
      const existing = new Set(
        Array.from(list.querySelectorAll(".cart-item[data-variant-id]")).map((el) =>
          String(el.dataset.variantId)
        )
      );
      const missing = ids.filter((id) => !existing.has(String(id))).slice(0, 2);
      if (!missing.length) return;
      list.insertAdjacentHTML("beforeend", missing.map(ghostRowMarkup).join(""));
      return;
    }

    // Empty cart → first add: placeholder rows instead of a blank drawer.
    const rows = Math.max(1, Math.min(ids.length || 1, 3));
    body.insertAdjacentHTML(
      "afterbegin",
      '<div class="cart-drawer__ghost" aria-hidden="true">' +
        '<div class="cart__items">' + Array.from({ length: rows }, ghostRowMarkup).join("") + "</div>" +
        "</div>"
    );
  }

  function applyLoadingState(opts) {
    const body = getDrawerBody();
    if (!body) return;
    body.setAttribute("aria-busy", "true");
    body.classList.add("is-loading");
    body.querySelectorAll("button, input, select, textarea").forEach((el) => {
      if (!el.disabled) {
        el.disabled = true;
        el.dataset.cdDisabled = "1";
      }
    });
    if (opts && opts.ghostVariantIds) addGhostRows(body, opts.ghostVariantIds);
    updateCartDrawerBusyState();
  }

  function clearLoadingState() {
    const body = getDrawerBody();
    if (body) {
      body.setAttribute("aria-busy", "false");
      body.classList.remove("is-loading");
      body.querySelectorAll("[data-cd-disabled]").forEach((el) => {
        el.disabled = false;
        delete el.dataset.cdDisabled;
      });
      body.querySelectorAll(".cart-item--ghost, .cart-drawer__ghost").forEach((el) => el.remove());
    }
    updateCartDrawerBusyState();
  }

  function beginLoading(opts) {
    loadingDepth += 1;
    applyLoadingState(opts);
  }

  function endLoading() {
    loadingDepth = Math.max(0, loadingDepth - 1);
    // The body may have been re-rendered meanwhile: re-apply if work is still pending.
    if (loadingDepth > 0) applyLoadingState();
    else clearLoadingState();
  }

  // ---------------------------------------------------------------------------
  // BOGO helpers
  // ---------------------------------------------------------------------------
  /** Get all BOGO line items for a variant (original + discounted rows). */
  function getBogoLineItems(variantId) {
    const cartDrawer = getDrawer();
    if (!cartDrawer) return [];
    return Array.from(
      cartDrawer.querySelectorAll(
        `.cart-item[data-bogo="true"][data-variant-id="${variantId}"]`
      )
    ).map((el) => {
      const input = el.querySelector("[data-quantity-input]");
      return {
        key: el.dataset.itemId,
        el,
        qty: parseInt(input?.value, 10) || 0,
      };
    });
  }

  /** Build updates object to set each BOGO line to a share of newTotal (even split). */
  function splitBogoUpdates(items, newTotal) {
    const updates = {};
    const n = items.length;
    if (n === 0) return updates;
    let remaining = Math.max(0, newTotal);
    items.forEach((item, i) => {
      const part =
        i === n - 1 ? remaining : Math.ceil(remaining / (n - i));
      updates[item.key] = part;
      remaining -= part;
    });
    return updates;
  }

  /** Checkout: just go to checkout (no adding 1; quantities are kept even by up/down/remove). */
  async function ensureBogoEvenThenCheckout() {
    // A free-gift correction may still be debounced: apply it before leaving.
    await flushGiftReconcile();
    window.location.href = getLocalizedUrl('/checkout');
  }

  function handleCartError(error) {
    console.error("Cart Error:", error);
    const drawer = getDrawer();
    if (!drawer) return;
    const errorElement = document.createElement("div");
    errorElement.className = "cart-error";
    errorElement.setAttribute("role", "alert");
    errorElement.textContent = (error && error.error) || "Cart operation failed";
    drawer.prepend(errorElement);
    setTimeout(() => errorElement.remove(), 3000);
  }

  // ---------------------------------------------------------------------------
  // Cart mutations
  // ---------------------------------------------------------------------------
  async function handleQuantityChange(element, change) {
    const quantityControl = element.closest(".cart-item");
    if (!quantityControl) return;
    const input = quantityControl.querySelector("[data-quantity-input]");
    const itemKey = quantityControl.dataset.itemId;
    const variantId = quantityControl.dataset.variantId;
    const maxQuantity = parseInt(quantityControl.dataset.maxQuantity);
    const isBogo = quantityControl.dataset.bogo === "true";

    // BOGO: rebalance all lines for this variant (even split)
    if (isBogo && variantId) {
      const items = getBogoLineItems(variantId);
      if (items.length === 0) return;

      const isDown = quantityControl.dataset.bogoDown === "true";
      if (isDown) delete quantityControl.dataset.bogoDown;

      // Re-read qtys; use the current input.value for the line we're editing (so typed value is correct)
      const editedValue = parseInt(input.value, 10) || 0;
      items.forEach((it) => {
        if (it.el === quantityControl) {
          it.qty = editedValue;
        } else {
          const inp = it.el.querySelector("[data-quantity-input]");
          it.qty = parseInt(inp?.value, 10) || 0;
        }
      });
      const currentTotal = items.reduce((s, i) => s + i.qty, 0);
      let newTotal;

      if (isDown) {
        // Only when user clicked quantity-down: round down to even, min 2 (1 and 1)
        newTotal = currentTotal % 2 !== 0
          ? Math.max(2, currentTotal - 1)
          : Math.max(2, currentTotal - 2);
      } else {
        // Typed or quantity-up: round up to even (e.g. 1+4=5 → 6 → 3 and 3)
        newTotal = currentTotal % 2 !== 0 ? currentTotal + 1 : currentTotal;
      }

      const maxTotal = maxQuantity > 0 ? items.length * maxQuantity : 99999;
      newTotal = Math.min(newTotal, maxTotal);

      const updates = splitBogoUpdates(items, newTotal);
      quantityControl.classList.add("updating");
      setLineBusy(quantityControl, true);
      try {
        const response = await fetch(getLocalizedUrl('/cart/update.js'), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || "Cart update failed");
        await refreshCartContent({ quiet: true });
        if (canPublish()) {
          publish(PUB_SUB_EVENTS.cartUpdate, { action: "update", cart: data, source: "cart-drawer" });
        }
      } catch (err) {
        if (canPublish()) {
          publish(PUB_SUB_EVENTS.cartError, { error: err.message, context: "update-quantity" });
        }
      } finally {
        quantityControl.classList.remove("updating");
        input.classList.remove("updating");
        setLineBusy(quantityControl, false);
      }
      return;
    }

    // Non-BOGO: single line update
    let newQuantity =
      change === 0 ? parseInt(input.value) || 1 : Number(input.value) + change;

    if (maxQuantity > 0 && newQuantity > maxQuantity) {
      showStockError(quantityControl, `Only ${maxQuantity} available`);
      newQuantity = maxQuantity;
    }
    if (newQuantity < 1) newQuantity = 1;

    input.value = newQuantity;
    quantityControl.classList.add("updating");
    setLineBusy(quantityControl, true);

    try {
      const response = await fetch(getLocalizedUrl('/cart/change.js'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: itemKey, quantity: newQuantity }),
      });
      const cartData = await response.json();
      if (!response.ok) throw new Error(cartData.description || "Cart update failed");
      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartUpdate, { action: "update", itemKey, quantity: newQuantity, cart: cartData, source: "cart-drawer" });
      }
      await refreshCartContent({ quiet: true });
    } catch (error) {
      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartError, { error: error.message, context: "update-quantity" });
      }
      input.value = Number(input.value) - change;
    } finally {
      quantityControl.classList.remove("updating");
      setLineBusy(quantityControl, false);
    }
  }

  async function handleRemoveItem(button) {
    const itemElement = button.closest(".cart-item");
    if (!itemElement) return;
    const itemKey = itemElement.dataset.itemId;
    const variantId = itemElement.dataset.variantId;
    const isBogo = itemElement.dataset.bogo === "true";

    let loadingStarted = false;
    try {
      button.disabled = true;
      itemElement.classList.add("removing");
      updateCartDrawerBusyState();

      let updates;
      if (isBogo && variantId) {
        const bogoItems = getBogoLineItems(variantId);
        const currentTotal = bogoItems.reduce((s, i) => s + i.qty, 0);
        const newTotal = currentTotal >= 8 ? Math.max(0, currentTotal - 4) : Math.max(0, currentTotal - 2);
        if (newTotal === 0) {
          updates = {};
          bogoItems.forEach((it) => { updates[it.key] = 0; });
        } else {
          updates = splitBogoUpdates(bogoItems, newTotal);
        }
      } else {
        updates = { [itemKey]: 0 };
      }

      setLineBusy(itemElement, true);
      loadingStarted = true;

      const response = await fetch(getLocalizedUrl('/cart/update.js'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates }),
      });

      const cartData = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(cartData.description || "Remove failed");

      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartUpdate, { action: "remove", itemKey, cart: cartData, source: "cart-drawer" });
      }
      await refreshCartContent({ quiet: true });
    } catch (error) {
      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartError, { error: error.message, context: "remove-item" });
      }
      button.disabled = false;
      itemElement.classList.remove("removing");
    } finally {
      if (loadingStarted) setLineBusy(itemElement, false);
      updateCartDrawerBusyState();
    }
  }

  /**
   * Re-render the drawer from the `cart` section (which renders the
   * cart-drawer snippet, including the upgrades carousel). Shows the skeleton
   * for the duration of the fetch.
   * With { quiet: true } there is no skeleton: the new HTML is swapped in already
   * visible (line-level busy states are handled by the callers).
   * @param {{ghostVariantIds?: string[], quiet?: boolean}} [opts]
   */
  async function refreshCartContent(opts) {
    const cartDrawer = getDrawer();
    if (!cartDrawer || !getDrawerBody()) return;

    const quiet = !!(opts && opts.quiet);
    const seq = ++refreshSeq;
    if (!quiet) beginLoading(opts);

    try {
      const [sectionResponse, cartResponse] = await Promise.all([
        fetch(getLocalizedUrl('/') + '?section_id=cart', { headers: { Accept: "text/html" } }),
        fetch(getLocalizedUrl('/cart.js'), { headers: { Accept: "application/json" } }),
      ]);
      if (!sectionResponse.ok) throw new Error("Failed to fetch cart content");

      const text = await sectionResponse.text();
      const updatedCartData = cartResponse.ok ? await cartResponse.json().catch(() => null) : null;

      // A newer refresh was started while this one was in flight — let it render.
      if (seq !== refreshSeq) return;

      const doc = new DOMParser().parseFromString(text, "text/html");
      const newCartDrawer = doc.querySelector("#cart-drawer");
      if (!newCartDrawer) throw new Error("Cart drawer not found in response");

      // Quiet refresh: the images are already in the browser cache, so render them as
      // loaded (no skeleton / lazy-fade flash).
      if (quiet) markImagesReady(newCartDrawer);

      // Replace the entire drawer content
      cartDrawer.innerHTML = newCartDrawer.innerHTML;

      // Clear all pending timeouts since cart items changed
      pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      pendingTimeouts.clear();

      // Per-render init (carousel); listeners are delegated and already bound
      initializeCartDrawer();

      if (updatedCartData) {
        document.querySelectorAll("[data-cart-count]").forEach((el) => {
          el.textContent = updatedCartData.item_count;
        });
      }
      // The milestone bar is server-rendered in the new HTML; just make sure the free
      // gift matches the fresh cart (debounced; a no-op when nothing needs to change).
      scheduleGiftReconcile(updatedCartData ? giftStateFromCart(updatedCartData) : null);
      // Lets the PDP milestone bar resync after re-renders that don't publish (tab shown
      // again, back/forward). detail.basis = cart total excluding the gift, in cents.
      const giftStateEl = cartDrawer.querySelector("[data-cart-gift-state]");
      document.dispatchEvent(new CustomEvent("cart-drawer:rendered", {
        detail: { basis: giftStateEl ? parseInt(giftStateEl.dataset.cartExGiftCents, 10) : null },
      }));
    } catch (error) {
      console.error("Error refreshing cart content:", error);
    } finally {
      if (!quiet) endLoading();
    }
  }

  function markImagesReady(root) {
    root.querySelectorAll("img[data-skeleton], img[data-skeleton-keep]").forEach((img) => {
      img.classList.add("is-loaded");
      const box = img.closest(".skeleton");
      if (box) box.classList.add("is-ready");
    });
    root.querySelectorAll("img.lazy-fade").forEach((img) => {
      img.classList.add("loaded");
      const wrap = img.closest(".lazy-wrapper");
      if (wrap) {
        wrap.classList.remove("loading");
        wrap.classList.add("loaded");
      }
    });
  }

  // Line-level busy state (no drawer-wide skeleton): soft overlay + spinner on one line.
  let lineBusyDepth = 0;
  function setLineBusy(row, on) {
    lineBusyDepth = Math.max(0, lineBusyDepth + (on ? 1 : -1));
    if (row) {
      row.classList.toggle("is-line-busy", on);
      row.setAttribute("aria-busy", on ? "true" : "false");
    }
  }
  function isDrawerBusy() {
    return loadingDepth > 0 || lineBusyDepth > 0;
  }

  // ---------------------------------------------------------------------------
  // Free gift (milestone 2 of the rewards bar; ported from ST-Base reconcileFreeGift).
  // Config + current state come from [data-cart-gift-state] inside the drawer
  // (snippets/cart-drawer.liquid), which is re-rendered on every refresh.
  //   basis >= threshold (cart total EXCLUDING the gift) -> gift x1 (property _gift)
  //   basis <  threshold, or no other items              -> gift removed
  //   gift qty != 1 / duplicate gift lines               -> forced back to one line x1
  //   a stale gift line (a _gift / hidden-gift line that is not the current gift
  //   product, e.g. the old brush after the setting changed) -> removed, and the
  //   current gift is added when the cart still qualifies
  // Hooked into refreshCartContent (every drawer re-render schedules it). Debounced,
  // skipped while any other cart mutation is in flight (it is rescheduled), never
  // opens the drawer, and its own follow-up refresh is quiet (no skeleton). The
  // follow-up refresh reconciles again but finds nothing to change, so it settles.
  // ---------------------------------------------------------------------------
  const GIFT_DEBOUNCE_MS = 450;
  const GIFT_MAX_FIXES_PER_MIN = 6; // circuit breaker against any server-side ping-pong
  let giftTimer = null;
  let giftBusy = false;
  let giftPromise = null;
  let giftFixLog = [];
  let giftFailedAtBasis = null; // a failed add is not retried until the cart total changes

  function giftConfig() {
    const el = document.querySelector("#cart-drawer [data-cart-gift-state]");
    if (!el) return null;
    const variantId = parseInt(el.dataset.giftVariantId, 10);
    const threshold = parseInt(el.dataset.giftThresholdCents, 10);
    if (!variantId || !threshold) return null;
    return { el, variantId, threshold };
  }

  function giftStateFromDom() {
    const cfg = giftConfig();
    if (!cfg) return null;
    const d = cfg.el.dataset;
    return {
      basis: parseInt(d.cartExGiftCents, 10) || 0,
      giftQty: parseInt(d.giftQty, 10) || 0,
      giftLines: parseInt(d.giftLines, 10) || 0,
      stale: parseInt(d.giftStale, 10) || 0,
      others: parseInt(d.nongiftCount, 10) || 0,
    };
  }

  function giftLinesOf(cart, variantId) {
    return (cart.items || []).filter(
      (i) => i.variant_id === variantId || (i.properties && i.properties._gift === "true")
    );
  }

  function giftStateFromCart(cart) {
    const cfg = giftConfig();
    if (!cfg || !cart) return null;
    const lines = giftLinesOf(cart, cfg.variantId);
    const giftQty = lines.reduce((s, i) => s + i.quantity, 0);
    const giftCents = lines.reduce((s, i) => s + (i.final_line_price || 0), 0);
    return {
      basis: Math.max(0, (cart.total_price || 0) - giftCents),
      giftQty,
      giftLines: lines.length,
      stale: lines.filter((i) => i.variant_id !== cfg.variantId).length,
      others: (cart.item_count || 0) - giftQty,
    };
  }

  /** "add" | "remove" | "fix" | null for a given state. */
  function giftAction(state, threshold) {
    if (!state) return null;
    const want = state.others > 0 && state.basis >= threshold;
    if (want && state.giftQty === 0) return "add";
    if (!want && state.giftQty > 0) return "remove";
    if (want && (state.giftQty !== 1 || state.giftLines > 1 || state.stale > 0)) return "fix";
    return null;
  }

  function cartMutationInFlight() {
    return isDrawerBusy() || crossSellBusy || isQuickAdding || !!pendingQuantityUpdate || pendingTimeouts.size > 0;
  }

  function scheduleGiftReconcile(state) {
    const cfg = giftConfig();
    if (!cfg) return;
    // Cheap pre-check on the state we already have: nothing to do -> no timer, no request.
    if (state && !giftAction(state, cfg.threshold)) {
      if (giftTimer) { clearTimeout(giftTimer); giftTimer = null; }
      return;
    }
    if (giftTimer) clearTimeout(giftTimer);
    giftTimer = setTimeout(() => {
      giftTimer = null;
      giftPromise = reconcileFreeGift().finally(() => { giftPromise = null; });
    }, GIFT_DEBOUNCE_MS);
  }

  async function reconcileFreeGift() {
    const cfg = giftConfig();
    if (!cfg || giftBusy) return;
    // Another cart mutation is running: its own refresh will reschedule us; retry anyway.
    if (cartMutationInFlight()) {
      scheduleGiftReconcile(null);
      return;
    }
    const now = Date.now();
    giftFixLog = giftFixLog.filter((t) => now - t < 60000);
    if (giftFixLog.length >= GIFT_MAX_FIXES_PER_MIN) {
      console.warn("[free-gift] too many corrections in a minute, pausing");
      return;
    }

    giftBusy = true;
    try {
      // Always decide on the live cart, not on the rendered HTML.
      const res = await fetch(getLocalizedUrl("/cart.js"), { headers: { Accept: "application/json" }, cache: "no-store" });
      if (!res.ok) return;
      const cart = await res.json();
      const state = giftStateFromCart(cart);
      const action = giftAction(state, cfg.threshold);
      if (!action) return;
      if (action === "add" && giftFailedAtBasis === state.basis) return;
      // The shopper started something else while we were fetching: let that finish first.
      if (cartMutationInFlight()) {
        scheduleGiftReconcile(null);
        return;
      }

      const lines = giftLinesOf(cart, cfg.variantId);
      let r;
      if (action === "add") {
        r = await fetch(getLocalizedUrl("/cart/add.js"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: cfg.variantId, quantity: 1, properties: { _gift: "true" } }] }),
        });
        giftFailedAtBasis = r.ok ? null : state.basis;
      } else {
        // remove: every gift line -> 0; fix: keep the first CURRENT-gift line at 1,
        // drop the rest (duplicates and stale gifts of another product).
        const updates = {};
        const keep = action === "fix" ? lines.find((l) => l.variant_id === cfg.variantId) : null;
        lines.forEach((l) => { updates[l.key] = l === keep ? 1 : 0; });
        r = await fetch(getLocalizedUrl("/cart/update.js"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates }),
        });
        // Only stale gift lines were there: add the current gift in their place.
        if (r.ok && action === "fix" && !keep) {
          r = await fetch(getLocalizedUrl("/cart/add.js"), {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [{ id: cfg.variantId, quantity: 1, properties: { _gift: "true" } }] }),
          });
          giftFailedAtBasis = r.ok ? null : state.basis;
        }
      }
      giftFixLog.push(Date.now());
      if (!r.ok) {
        console.warn("[free-gift] " + action + " failed (" + r.status + ")");
        return;
      }
      // "update" (never "add"): handleCartUpdate ignores it, so the drawer is NOT opened.
      if (canPublish()) publish(PUB_SUB_EVENTS.cartUpdate, { action: "update", source: "free-gift-auto" });
      await refreshCartContent({ quiet: true });
    } catch (err) {
      console.warn("[free-gift] reconcile failed", err);
    } finally {
      giftBusy = false;
    }
  }

  /** Run a pending reconcile now (checkout must not leave with a stale gift). */
  async function flushGiftReconcile() {
    if (giftTimer) {
      clearTimeout(giftTimer);
      giftTimer = null;
      giftPromise = reconcileFreeGift().finally(() => { giftPromise = null; });
    }
    if (giftPromise) {
      await Promise.race([giftPromise, new Promise((r) => setTimeout(r, 4000))]);
    }
  }

  async function handleQuickAdd(form) {
    // Prevent multiple simultaneous submissions
    if (isQuickAdding) return;
    isQuickAdding = true;

    // Get form elements
    const submitButton = form.querySelector('[type="submit"]');
    const spinner = form.querySelector(".loading-spinner-quick");
    const buttonText = submitButton?.querySelector(".button-text");
    const originalText = buttonText?.innerHTML || "";
    let failed = false;
    let loadingStarted = false;

    try {
      // Set loading state
      form.classList.add("is-loading");
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute("aria-busy", "true");
        if (spinner) spinner.style.display = "block";
      }

      const formData = new FormData(form);
      const variantId = formData.get("id");
      // Respect the form's quantity (e.g. 2 for Buy-1-Get-1 bundles); default 1.
      const quantity = Math.max(1, parseInt(formData.get("quantity"), 10) || 1);

      if (!variantId) {
        throw new Error("No variant ID found");
      }

      beginLoading({ ghostVariantIds: [String(variantId)] });
      loadingStarted = true;

      const addUrl = (window.routes && window.routes.cart_add_url) || getLocalizedUrl('/cart/add');
      const response = await fetch(addUrl + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || "Failed to add to cart");
      }

      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          variant: data,
          formData: Object.fromEntries(formData),
          product: {},
          source: "cart-drawer", // handleCartUpdate skips this; we refresh below
        });
      }

      // Refresh cart content (the added bundle is now in the cart, so it
      // drops out of the upgrades carousel on re-render)
      await refreshCartContent();

      // Open cart drawer if not already open
      const cartDrawer = getDrawer();
      if (cartDrawer && !cartDrawer.classList.contains("open")) {
        openDrawer();
      }
    } catch (error) {
      console.error("Quick add error:", error);
      failed = true;

      if (canPublish()) {
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "add-to-cart",
        });
      }
    } finally {
      isQuickAdding = false;
      if (loadingStarted) endLoading();

      // Reset button state (no-op if the form was replaced by the re-render)
      form.classList.remove("is-loading");
      if (spinner) spinner.style.display = "none";
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute("aria-busy");
        if (buttonText && failed) {
          buttonText.textContent = document.documentElement.lang === "ar" ? "حاول مجددًا" : "Try again";
          setTimeout(() => {
            buttonText.innerHTML = originalText;
          }, 2000);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // "Recommended" slider (snippets/featured-slider.liquid inside .cart-upsell).
  // The drawer HTML is replaced on refresh, so the new slider needs binding;
  // initFeaturedSlider guards against double init. Its product-card forms are
  // handled by the delegated quick-add above (card-product.js skips #cart-drawer).
  // ---------------------------------------------------------------------------
  function initRecommendationsSlider() {
    const drawer = getDrawer();
    if (!drawer || typeof window.initFeaturedSlider !== "function") return;
    drawer.querySelectorAll(".cart-upsell .featured-slider").forEach((el) => window.initFeaturedSlider(el));
  }

  // ---------------------------------------------------------------------------
  // Cross-sell rows (snippets/cart-upgrades.liquid, at most one per line item).
  // "+ Add" (data-cupg="add") ADDS one unit of the suggested product as its own line;
  // it never swaps, replaces or removes anything. The drawer is then re-rendered
  // (section rendering API), which recomputes every line's suggestion.
  // ---------------------------------------------------------------------------
  let crossSellBusy = false;

  function showCrossSellError(btn, kind) {
    const wrap = btn.closest(".cart-item__upgrade");
    if (!wrap) return;
    const ar = document.body.classList.contains("rtl");
    let el = wrap.querySelector(".cart-item__upgrade-error");
    if (!el) {
      el = document.createElement("p");
      el.className = "cart-item__upgrade-error";
      el.setAttribute("role", "alert");
      wrap.appendChild(el);
    }
    const msgs = {
      unavailable: ar ? "تعذّر الإضافة، هذا المنتج غير متاح حاليًا." : "Couldn’t add. That item isn’t available right now.",
      network: ar ? "مشكلة في الاتصال. جرّب تاني." : "Connection problem. Please try again.",
    };
    el.textContent = msgs[kind] || msgs.network;
    setTimeout(() => el.remove(), 4000);
  }

  async function handleCrossSellAdd(btn) {
    const d = btn.dataset;
    // One add at a time (also covers rapid double taps: the button is disabled while busy).
    if (crossSellBusy || isDrawerBusy() || btn.disabled) return;
    const addId = Number(d.addVariant);
    if (!addId) return;

    crossSellBusy = true;
    btn.disabled = true;
    btn.classList.add("is-loading");
    btn.setAttribute("aria-busy", "true");
    const lineKey = d.lineKey;
    const row = btn.closest(".cart-item");
    setLineBusy(row, true);
    const others = Array.from(document.querySelectorAll("#cart-drawer [data-cupg]")).filter((b) => b !== btn);
    others.forEach((b) => { b.disabled = true; });
    let failKind = "network";
    let failed = false;
    try {
      const res = await fetch(getLocalizedUrl("/cart/add.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: addId, quantity: 1 }] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        failKind = res.status === 422 ? "unavailable" : "network";
        throw new Error(data.description || "Add failed");
      }
      if (canPublish()) {
        // source "cart-drawer": handleCartUpdate skips it; we refresh below.
        publish(PUB_SUB_EVENTS.cartUpdate, { action: "add", variant: data, source: "cart-drawer" });
      }
      await refreshCartContent({ quiet: true });
    } catch (error) {
      failed = true;
      console.warn("Cart cross-sell:", error.message);
    } finally {
      crossSellBusy = false;
      btn.disabled = false;
      btn.classList.remove("is-loading");
      btn.removeAttribute("aria-busy");
      setLineBusy(row, false);
      others.forEach((b) => { if (b.isConnected) b.disabled = false; });
    }
    if (failed) {
      const liveBtn =
        (lineKey && document.querySelector(`#cart-drawer .cart-item[data-item-id="${CSS.escape(lineKey)}"] [data-cupg]`)) ||
        (btn.isConnected ? btn : null);
      if (liveBtn) showCrossSellError(liveBtn, failKind);
    }
  }

  // Server-rendered drawer on page load: fix the gift only if its state says so
  // (no request otherwise). Kept last: the gift state above uses let/const.
  scheduleGiftReconcile(giftStateFromDom());
});
